import { useState, useEffect } from 'react';
import VERSION from '../config/version.json';
import DATA from '../config/new_data.json';


// Floating buttons for edit mode actions
export function EditorFloatingButtons({ savePatchFile, clearAllChanges, hasChanges, isEditMode }) {
  if (!isEditMode) return null;
  return (    
    <div style={{ position: 'fixed', top: 10, left: 10, zIndex: 1000, display: 'flex', gap: '10px' }}>
      { hasChanges && (
        <button
          style={{
            background: '#ffe',
            border: '2px solid #888',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
          onClick={savePatchFile}
        >Sauvegarde</button>
      )}
      <button
        style={{
          background: '#fcc',
          border: '2px solid #c44',
          borderRadius: '8px',
          padding: '8px 16px',
          fontWeight: 'bold',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
        onClick={() => {
          //savePatchFile();
          clearAllChanges();
        }}
      >Terminer</button>
    </div>
  );
}

// Encapsulates edit mode and title change logic for media editor
export function useEditorMode(category) {
  // Track all changes across pages, persistent at app level
  const [allTitleChanges, setAllTitleChanges] = useState(() => {
    const saved = sessionStorage.getItem('familleTitleChanges');
    return saved ? JSON.parse(saved) : {};
  });
  // Local changes for current page
  const [editTitles, setEditTitles] = useState({});
  // Edit mode is determined by ?edit in the URL
  const [isEditMode, setIsEditMode] = useState(() => window.location.search.includes('edit'));

  useEffect(() => {
    // Listen for URL changes to update edit mode
    const onPopState = () => {
      setIsEditMode(window.location.search.includes('edit'));
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  useEffect(() => {
    // Restore edits for this page
    setEditTitles(allTitleChanges[category] || {});
  }, [category, allTitleChanges]);

  useEffect(() => {
    sessionStorage.setItem('familleTitleChanges', JSON.stringify(allTitleChanges));
  }, [allTitleChanges]);

  const handleTitleChange = (idx, value) => {
    setEditTitles(prev => ({ ...prev, [idx]: value }));
    setAllTitleChanges(prev => {
      const pageKey = category;
      const changes = { ...(prev[pageKey] || {}) };
      changes[idx] = value;
      return { ...prev, [pageKey]: changes };
    });
  };

  const exitEditMode = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('edit')) {
      url.searchParams.delete('edit');
      window.history.replaceState({}, '', url);
      setIsEditMode(false);
    }
  };

  const enterEditMode = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('edit')) {
      url.searchParams.set('edit', '');
      window.history.replaceState({}, '', url);
      setIsEditMode(true);
    }
  };

  const clearAllChanges = () => {
    setAllTitleChanges({});
    setEditTitles({});
    exitEditMode();
  };

  const savePatchFile = () => {
    // Save changes for all pages
    const patchLines = [];
    Object.entries(allTitleChanges).forEach(([page, changes]) => {
      Object.entries(changes).forEach(([idx, newTitle]) => {
        const entry = DATA?.[page]?.[Number(idx)];
        const url = entry?.url || entry?.poster || '';
        patchLines.push(`${VERSION.version}:${page}:${idx}:[${url}]:${newTitle}`);
      });
    });
    const patchContent = patchLines.join('\n');
    const blob = new Blob([patchContent], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `famille_title_patch_${VERSION.version}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const hasUnsavedChanges = Object.values(allTitleChanges).some(
    (pageChanges) => Object.keys(pageChanges).length > 0
  );

  return {
    isEditMode,
    editTitles,
    handleTitleChange,
    enterEditMode,
    exitEditMode,
    clearAllChanges,
    savePatchFile,
    hasChanges: hasUnsavedChanges,
  };
}
